import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Users, Activity, FileText, Search, Shield, ShieldCheck, ShieldAlert, RefreshCw, Send, Loader2 } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import AdminBroadcastTab from "@/components/AdminBroadcastTab";

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [usage, setUsage] = useState(null);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/admin/usage").then(({ data }) => setUsage(data)).catch(() => {});
    api.get("/admin/subscriptions").then(({ data }) => setSubs(data)).catch(() => {});
  }, []);

  return (
    <DashboardLayout eyebrow="Operations" title="Admin Panel" subtitle="Users, AI usage and subscriptions across the platform.">
      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={Users} label="Users" value={usage?.totals?.users ?? users.length} testid="admin-stat-users" />
        <Stat icon={Activity} label="AI Calls" value={usage?.totals?.ai_calls ?? 0} testid="admin-stat-calls" />
        <Stat icon={Search} label="Audits" value={usage?.totals?.audits ?? 0} testid="admin-stat-audits" />
        <Stat icon={FileText} label="Blogs" value={usage?.totals?.blogs ?? 0} testid="admin-stat-blogs" />
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="admin-tabs">
          <TabsTrigger value="users" className="rounded-sm data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="usage" className="rounded-sm data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black" data-testid="admin-tab-usage">AI Usage</TabsTrigger>
          <TabsTrigger value="subscriptions" className="rounded-sm data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black" data-testid="admin-tab-subs">Subscriptions</TabsTrigger>
          <TabsTrigger value="security" className="rounded-sm data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black" data-testid="admin-tab-security">Cross-Account Protection</TabsTrigger>
          <TabsTrigger value="broadcast" className="rounded-sm data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black" data-testid="admin-tab-broadcast">Broadcast</TabsTrigger>
          <TabsTrigger value="leads" className="rounded-sm data-[state=active]:bg-[#F59E0B] data-[state=active]:text-black" data-testid="admin-tab-leads">Free Audit Leads</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden" data-testid="admin-users-list">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800">
                  <th className="px-6 py-3 font-medium">User</th>
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Provider</th>
                  <th className="px-6 py-3 font-medium">Role</th>
                  <th className="px-6 py-3 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-zinc-900 last:border-0 hover:bg-zinc-900/50" data-testid={`admin-user-${u.user_id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8 rounded-sm">
                          <AvatarImage src={u.picture} />
                          <AvatarFallback className="bg-[#0055FF]/20 text-[#3377FF] rounded-sm text-xs">
                            {(u.name || u.email).slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-zinc-200">{u.name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-zinc-400">{u.email}</td>
                    <td className="px-6 py-4">
                      <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-400 uppercase text-[10px]">{u.auth_provider}</Badge>
                    </td>
                    <td className="px-6 py-4">
                      {u.role === "admin"
                        ? <Badge className="bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/30 rounded-sm">Admin</Badge>
                        : <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-400">User</Badge>}
                    </td>
                    <td className="px-6 py-4 text-xs text-zinc-500 font-mono">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="usage" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6" data-testid="admin-usage-chart">
              <span className="label-eyebrow">Calls by feature</span>
              <div className="h-64 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={usage?.by_feature || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="feature" stroke="#71717A" fontSize={11} />
                    <YAxis stroke="#71717A" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #27272A" }} />
                    <Bar dataKey="count" fill="#0055FF" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="admin-usage-recent">
              <div className="px-6 py-4 border-b border-zinc-800">
                <span className="label-eyebrow">Recent activity</span>
              </div>
              <ul className="divide-y divide-zinc-900 max-h-80 overflow-y-auto">
                {(usage?.recent || []).map((r) => (
                  <li key={r.id} className="px-6 py-3 flex items-center justify-between text-sm">
                    <span className="text-zinc-300 capitalize">{r.feature.replace("_", " ")}</span>
                    <span className="text-xs text-zinc-500 font-mono">{new Date(r.created_at).toLocaleString()}</span>
                  </li>
                ))}
                {(!usage?.recent || usage.recent.length === 0) && (
                  <li className="px-6 py-12 text-center text-zinc-500 text-sm">No activity yet</li>
                )}
              </ul>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="subscriptions" className="mt-6">
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="admin-subs-list">
            <div className="px-6 py-4 border-b border-zinc-800">
              <span className="label-eyebrow">{subs.length} subscriptions</span>
            </div>
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800">
                  <th className="px-6 py-3 font-medium">User</th>
                  <th className="px-6 py-3 font-medium">Plan</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Since</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.user_id} className="border-b border-zinc-900 last:border-0">
                    <td className="px-6 py-4">
                      <div className="text-zinc-200">{s.name || "—"}</div>
                      <div className="text-xs text-zinc-500">{s.email}</div>
                    </td>
                    <td className="px-6 py-4"><Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-300">{s.tier || "Starter"}</Badge></td>
                    <td className="px-6 py-4">
                      <Badge className="bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 rounded-sm">{s.status || "active"}</Badge>
                    </td>
                    <td className="px-6 py-4 text-xs text-zinc-500 font-mono">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="security" className="mt-6">
          <SecurityTab />
        </TabsContent>

        <TabsContent value="broadcast" className="mt-6">
          <AdminBroadcastTab />
        </TabsContent>

        <TabsContent value="leads" className="mt-6">
          <LeadsTab />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function SecurityTab() {
  const [status, setStatus] = useState({ loading: true, registered: false, response: null, error: null });
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setStatus((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get("/risc/status");
      setStatus({ loading: false, registered: !!data.registered, response: data.google_response, error: null });
    } catch (e) {
      setStatus({ loading: false, registered: false, response: null, error: formatApiError(e) });
    }
    try {
      const { data } = await api.get("/risc/recent?limit=20");
      setEvents(data.items || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const run = async (key, fn, successMsg) => {
    setBusy(key);
    try {
      await fn();
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="admin-security-tab">
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-sm grid place-items-center" style={{ background: "#10B98115", border: "1px solid #10B98140" }}>
            {status.registered ? <ShieldCheck className="w-6 h-6 text-[#10B981]" /> : <ShieldAlert className="w-6 h-6 text-amber-400" />}
          </div>
          <div className="flex-1 min-w-[280px]">
            <h3 className="font-display text-lg font-semibold">Google Cross-Account Protection (RISC)</h3>
            <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
              When a user's Google account is compromised, hijacked, or disabled, Google notifies us in real time. We then invalidate that user's RankPilot session before an attacker can exploit it.
            </p>
            {status.loading && <p className="text-xs text-zinc-500 mt-3 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Checking stream status…</p>}
            {!status.loading && status.registered && (
              <Badge className="bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 rounded-sm mt-3" data-testid="risc-status-registered">
                <Shield className="w-3 h-3 mr-1" /> Stream active
              </Badge>
            )}
            {!status.loading && !status.registered && (
              <div className="mt-3 text-xs text-amber-300 bg-amber-950/20 border border-amber-700/40 rounded-sm p-3" data-testid="risc-status-not-registered">
                <div className="font-medium mb-1">Not yet registered with Google</div>
                <p className="text-amber-200/80">{status.error || "Click 'Register stream' to start receiving events."}</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={() => run("register", () => api.post("/risc/register", {}), "Stream registered with Google")}
                  disabled={!!busy} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-9" data-testid="risc-register-btn">
            {busy === "register" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Shield className="w-3.5 h-3.5 mr-1.5" />}
            {status.registered ? "Re-register" : "Register stream"}
          </Button>
          <Button onClick={() => run("verify", () => api.post("/risc/test-verify"), "Verification ping sent — check Recent events in a few seconds")}
                  disabled={!!busy || !status.registered} variant="outline"
                  className="bg-zinc-950 border-zinc-800 rounded-sm h-9" data-testid="risc-verify-btn">
            {busy === "verify" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
            Send test ping
          </Button>
          <Button onClick={load} disabled={!!busy} variant="outline"
                  className="bg-zinc-950 border-zinc-800 rounded-sm h-9" data-testid="risc-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        {status.response && (
          <pre className="mt-4 text-[10px] text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-sm p-3 overflow-x-auto max-h-40" data-testid="risc-config-json">
{JSON.stringify(status.response, null, 2)}
          </pre>
        )}
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="risc-events-list">
        <div className="px-5 py-3 border-b border-zinc-800 text-xs uppercase tracking-widest text-zinc-500 flex items-center justify-between">
          <span>Recent security events</span>
          <span className="text-zinc-600">{events.length} shown</span>
        </div>
        {events.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">No events received yet. Send a test ping above to verify the round-trip.</div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {events.map((e, i) => {
              const short = (e.event_type || "").split("/").pop();
              const sub = e.payload?.subject?.sub || e.payload?.subject?.subject_id || "—";
              return (
                <div key={i} className="px-5 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`risc-event-${i}`}>
                  <Badge className="bg-[#0055FF]/15 text-[#3377FF] border-[#0055FF]/30 rounded-sm">{short}</Badge>
                  <span className="text-zinc-400 font-mono text-xs truncate flex-1">sub={sub}</span>
                  <span className="text-zinc-600 text-xs">{e.received_at ? new Date(e.received_at).toLocaleString() : ""}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="text-xs text-zinc-500 leading-relaxed">
        <strong className="text-zinc-300">Setup checklist:</strong>
        <ol className="list-decimal pl-5 mt-2 space-y-1">
          <li>In Cloud Console → IAM → Service accounts → create one with role <code className="bg-zinc-900 px-1 rounded">RISC Configuration Admin</code>.</li>
          <li>Generate a JSON key and paste its contents (or path) into the backend env var <code className="bg-zinc-900 px-1 rounded">GOOGLE_RISC_SERVICE_ACCOUNT_JSON</code>.</li>
          <li>In API Library, enable <strong>RISC API</strong>.</li>
          <li>Click <strong>Register stream</strong> above — it tells Google to POST to <code className="bg-zinc-900 px-1 rounded">/api/risc/events</code>.</li>
          <li>Click <strong>Send test ping</strong> to confirm round-trip works.</li>
        </ol>
      </div>
    </div>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/audit-requests")
      .then(({ data }) => setLeads(data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-zinc-500 py-8 text-center">Loading leads...</div>;
  if (leads.length === 0) {
    return (
      <div className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-12 text-center" data-testid="leads-empty">
        <Send className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
        <h3 className="font-display text-lg font-medium text-zinc-300">No free-audit leads yet</h3>
        <p className="text-sm text-zinc-500 mt-2">Share <code className="text-zinc-400">myrankpilotai.com/free-google-ads-audit</code> to start collecting leads.</p>
      </div>
    );
  }

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden" data-testid="leads-list">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800">
            <th className="px-4 py-3 font-medium">Email</th>
            <th className="px-4 py-3 font-medium">Website</th>
            <th className="px-4 py-3 font-medium">Customer ID</th>
            <th className="px-4 py-3 font-medium text-right">Monthly Spend</th>
            <th className="px-4 py-3 font-medium">Submitted</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l, i) => (
            <tr key={i} className="border-b border-zinc-800/50 last:border-0 hover:bg-zinc-900/30" data-testid={`lead-row-${i}`}>
              <td className="px-4 py-3 text-zinc-200">{l.email}</td>
              <td className="px-4 py-3"><a href={l.website} target="_blank" rel="noopener noreferrer" className="text-[#3377FF] hover:underline">{l.website}</a></td>
              <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{l.google_ads_customer_id || "—"}</td>
              <td className="px-4 py-3 text-right text-zinc-300">{l.monthly_ad_spend_usd ? `$${Number(l.monthly_ad_spend_usd).toLocaleString()}` : "—"}</td>
              <td className="px-4 py-3 text-zinc-500 text-xs">{l.created_at?.slice(0, 16).replace("T", " ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="label-eyebrow">{label}</span>
        <Icon className="w-4 h-4 text-zinc-500" />
      </div>
      <div className="font-display text-3xl font-semibold tracking-tighter">{value}</div>
    </div>
  );
}
